<template>
  <div class="mx-auto max-w-5xl space-y-5">
    <div v-if="message.text" class="rounded-xl border px-4 py-3 text-sm" :class="message.error ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'">
      {{ message.text }}
    </div>

    <section v-if="isLoading" class="rounded-2xl border border-zinc-200 bg-white p-12 text-center text-sm text-zinc-500 shadow-sm">
      Loading payout account...
    </section>

    <template v-else>
      <section v-if="!overview.profile?.usesPlatformaPayments" class="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
        <div class="grid gap-0 lg:grid-cols-[1fr_320px]">
          <div class="p-7 sm:p-9">
            <p class="text-xs font-bold uppercase tracking-[0.18em] text-[#2D8CFF]">Connected Stripe</p>
            <h3 class="mt-2 text-2xl font-semibold text-zinc-900">Stripe handles your payouts</h3>
            <p class="mt-3 max-w-xl text-sm leading-6 text-zinc-600">You do not need to attach a bank account here. Payments routed to your connected Stripe account follow the payout settings in Stripe.</p>
          </div>
          <div class="flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 p-8">
            <div class="flex h-24 w-24 items-center justify-center rounded-3xl bg-white text-[#2D8CFF] shadow-lg shadow-blue-100">
              <ArrowPathRoundedSquareIcon class="h-12 w-12" />
            </div>
          </div>
        </div>
      </section>

      <section v-else-if="bankAccount && !isEditing" class="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
        <div class="border-b border-zinc-100 bg-gradient-to-r from-white to-blue-50 px-6 py-5">
          <div class="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p class="text-xs font-bold uppercase tracking-[0.18em] text-[#2D8CFF]">Platforma Payments</p>
              <h3 class="mt-1 text-xl font-semibold text-zinc-900">Payout bank account</h3>
              <p class="mt-1 text-sm text-zinc-500">Used only for manually approved Platforma Payments payouts.</p>
            </div>
            <span class="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
              <CheckCircleIcon class="h-4 w-4" /> Active
            </span>
          </div>
        </div>
        <div class="grid gap-5 p-6 sm:grid-cols-2">
          <div class="rounded-xl border border-zinc-200 bg-zinc-50 p-5">
            <p class="text-xs font-semibold uppercase tracking-wide text-zinc-500">Account</p>
            <p class="mt-2 text-base font-semibold text-zinc-900">{{ bankAccount.displayLabel }}</p>
            <p class="mt-1 text-sm capitalize text-zinc-600">{{ bankAccount.accountType }} ending in {{ bankAccount.accountLast4 }}</p>
          </div>
          <div class="rounded-xl border border-zinc-200 bg-zinc-50 p-5">
            <p class="text-xs font-semibold uppercase tracking-wide text-zinc-500">Routing</p>
            <p class="mt-2 text-base font-semibold text-zinc-900">•••••{{ bankAccount.routingLast4 }}</p>
            <p class="mt-1 text-sm capitalize text-zinc-600">{{ bankAccount.holderType }} account · United States</p>
          </div>
        </div>
        <div class="flex flex-wrap justify-end gap-3 border-t border-zinc-100 px-6 py-4">
          <button type="button" class="h-10 rounded-lg border border-zinc-300 bg-white px-4 text-sm font-semibold text-zinc-700 hover:bg-zinc-50" @click="showRemoveConfirm = true">Remove account</button>
          <button type="button" class="h-10 rounded-lg bg-zinc-900 px-4 text-sm font-semibold text-white hover:bg-zinc-800" @click="isEditing = true">Replace account</button>
        </div>
      </section>

      <section v-else-if="overview.profile?.usesPlatformaPayments" class="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
        <div class="border-b border-zinc-100 bg-gradient-to-r from-white to-blue-50 px-6 py-5 sm:px-7">
          <p class="text-xs font-bold uppercase tracking-[0.18em] text-[#2D8CFF]">Secure payout setup</p>
          <h3 class="mt-1 text-xl font-semibold text-zinc-900">{{ bankAccount ? 'Replace bank account' : 'Attach a U.S. bank account' }}</h3>
          <p class="mt-1 max-w-2xl text-sm leading-6 text-zinc-500">Your full routing and account numbers are encrypted at rest. Only masked details appear after saving.</p>
        </div>

        <form class="space-y-7 p-6 sm:p-7" @submit.prevent="saveAccount">
          <fieldset class="space-y-4">
            <legend class="text-sm font-semibold text-zinc-900">Recipient</legend>
            <div class="grid gap-4 sm:grid-cols-2">
              <FormSelect v-model="form.holderType" label="Account holder type" :options="holderOptions" />
              <FormField v-model="form.nickname" label="Account nickname" placeholder="Operations checking" />
              <FormField v-if="form.holderType === 'business'" v-model="form.businessName" label="Business name" class="sm:col-span-2" required />
              <template v-else>
                <FormField v-model="form.firstName" label="First name" required />
                <FormField v-model="form.lastName" label="Last name" required />
              </template>
            </div>
          </fieldset>

          <fieldset class="space-y-4 border-t border-zinc-100 pt-6">
            <legend class="text-sm font-semibold text-zinc-900">Business or recipient address</legend>
            <div class="grid gap-4 sm:grid-cols-6">
              <FormField v-model="form.streetLine1" label="Street address" class="sm:col-span-6" required />
              <FormField v-model="form.city" label="City" class="sm:col-span-3" required />
              <FormSelect v-model="form.regionCode" label="State" class="sm:col-span-2" :options="stateOptions" />
              <FormField v-model="form.postalCode" label="ZIP code" class="sm:col-span-1" inputmode="numeric" required />
            </div>
          </fieldset>

          <fieldset class="space-y-4 border-t border-zinc-100 pt-6">
            <legend class="text-sm font-semibold text-zinc-900">Bank details</legend>
            <div class="grid gap-4 sm:grid-cols-2">
              <FormField v-model="form.bankName" label="Bank name" placeholder="Bank of America" />
              <FormSelect v-model="form.accountType" label="Account type" :options="accountTypeOptions" />
              <FormField v-model="form.routingNumber" label="Routing number" inputmode="numeric" autocomplete="off" maxlength="9" required />
              <FormField v-model="form.routingNumberConfirmation" label="Re-enter routing number" inputmode="numeric" autocomplete="off" maxlength="9" required />
              <FormField v-model="form.accountNumber" label="Account number" type="password" inputmode="numeric" autocomplete="new-password" maxlength="17" required />
              <FormField v-model="form.accountNumberConfirmation" label="Re-enter account number" type="password" inputmode="numeric" autocomplete="new-password" maxlength="17" required />
              <label class="block sm:col-span-2">
                <span class="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-zinc-500">Internal notes <span class="font-normal normal-case">(optional)</span></span>
                <textarea v-model.trim="form.notes" rows="3" class="app-scroll w-full resize-y rounded-lg border border-zinc-300 px-3 py-2.5 text-sm text-zinc-900 outline-none transition focus:border-[#2D8CFF] focus:ring-1 focus:ring-[#2D8CFF]" placeholder="Notes for your payout setup" />
              </label>
            </div>
          </fieldset>

          <div class="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800">
            Platforma will use this account only for your approved payouts. Saving it does not initiate a transfer.
          </div>
          <div class="flex flex-wrap justify-end gap-3">
            <button v-if="bankAccount" type="button" class="h-11 rounded-lg border border-zinc-300 bg-white px-5 text-sm font-semibold text-zinc-700 hover:bg-zinc-50" @click="isEditing = false">Cancel</button>
            <button type="submit" class="h-11 rounded-lg bg-[#2D8CFF] px-5 text-sm font-semibold text-white shadow-sm hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60" :disabled="isSaving">
              {{ isSaving ? 'Encrypting and saving...' : 'Save payout account' }}
            </button>
          </div>
        </form>
      </section>
    </template>

    <Teleport to="body">
      <div v-if="showRemoveConfirm" class="fixed inset-0 z-[100] flex items-center justify-center bg-zinc-950/30 p-4 backdrop-blur-sm" @click.self="showRemoveConfirm = false">
        <div class="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
          <div class="flex h-12 w-12 items-center justify-center rounded-xl bg-red-50 text-red-600">
            <BuildingLibraryIcon class="h-6 w-6" />
          </div>
          <h3 class="mt-4 text-lg font-semibold text-zinc-900">Remove payout account?</h3>
          <p class="mt-2 text-sm leading-6 text-zinc-600">Platforma Payments payouts will pause until another bank account is securely attached. Existing payout records will not be removed.</p>
          <div class="mt-6 flex justify-end gap-3">
            <button type="button" class="h-10 rounded-lg border border-zinc-300 bg-white px-4 text-sm font-semibold text-zinc-700 hover:bg-zinc-50" @click="showRemoveConfirm = false">Keep account</button>
            <button type="button" class="h-10 rounded-lg bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-700" @click="removeAccount">Remove account</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { defineComponent, h, onMounted, reactive, ref } from 'vue';
import { ArrowPathRoundedSquareIcon, BuildingLibraryIcon, CheckCircleIcon } from '@heroicons/vue/24/outline';
import PlatformaSelect from '@/Components/PlatformaSelect.vue';

const apiProxyOrigin = String(process.env.VUE_APP_API_PROXY_URL || '').replace(/\/+$/, '');
const apiBase = `${apiProxyOrigin}/api/creator-payments`;
const overview = reactive({ profile: null });
const bankAccount = ref(null);
const isLoading = ref(true);
const isSaving = ref(false);
const isEditing = ref(false);
const showRemoveConfirm = ref(false);
const message = reactive({ text: '', error: false });
const form = reactive({ holderType: 'business', nickname: '', businessName: '', firstName: '', lastName: '', streetLine1: '', city: '', regionCode: '', postalCode: '', bankName: '', accountType: 'checking', routingNumber: '', routingNumberConfirmation: '', accountNumber: '', accountNumberConfirmation: '', notes: '' });

const holderOptions = [{ value: 'business', label: 'Business account' }, { value: 'personal', label: 'Personal account' }];
const accountTypeOptions = [{ value: 'checking', label: 'Checking' }, { value: 'savings', label: 'Savings' }];
const stateOptions = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'].map((value) => ({ value, label: value }));

const fieldClass = 'h-11 w-full rounded-lg border border-zinc-300 bg-white px-3 text-sm text-zinc-900 outline-none transition focus:border-[#2D8CFF] focus:ring-1 focus:ring-[#2D8CFF]';
const FormField = defineComponent({
  inheritAttrs: false,
  props: { modelValue: { type: String, default: '' }, label: { type: String, required: true }, required: Boolean, type: { type: String, default: 'text' } },
  emits: ['update:modelValue'],
  setup(props, { attrs, emit }) {
    return () => h('label', { class: attrs.class || 'block' }, [
      h('span', { class: 'mb-1.5 block text-xs font-semibold uppercase tracking-wide text-zinc-500' }, `${props.label}${props.required ? ' *' : ''}`),
      h('input', { ...attrs, class: fieldClass, type: props.type, value: props.modelValue, required: props.required, onInput: (event) => emit('update:modelValue', event.target.value) }),
    ]);
  },
});
const FormSelect = defineComponent({
  inheritAttrs: false,
  props: { modelValue: { type: String, default: '' }, label: { type: String, required: true }, options: { type: Array, default: () => [] } },
  emits: ['update:modelValue'],
  setup(props, { attrs, emit }) {
    return () => h('label', { class: attrs.class || 'block' }, [
      h('span', { class: 'mb-1.5 block text-xs font-semibold uppercase tracking-wide text-zinc-500' }, props.label),
      h(PlatformaSelect, { modelValue: props.modelValue, options: props.options, 'onUpdate:modelValue': (value) => emit('update:modelValue', value) }),
    ]);
  },
});

const request = async (path, options = {}) => {
  const response = await fetch(`${apiBase}${path}`, { credentials: 'include', headers: { 'Content-Type': 'application/json', ...(options.headers || {}) }, ...options });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.message || 'Request failed');
  return payload;
};
const load = async () => {
  isLoading.value = true;
  message.text = '';
  try {
    const payload = await request('/creator/payout-overview');
    overview.profile = payload.profile || null;
    bankAccount.value = payload.bankAccount || null;
    isEditing.value = !bankAccount.value;
  } catch (error) {
    message.text = error.message;
    message.error = true;
  } finally { isLoading.value = false; }
};
const saveAccount = async () => {
  isSaving.value = true;
  message.text = '';
  try {
    const payload = await request('/creator/payout-bank-account', { method: 'PUT', body: JSON.stringify(form) });
    bankAccount.value = payload.bankAccount;
    isEditing.value = false;
    message.text = 'Your encrypted payout account was saved.';
    message.error = false;
    Object.assign(form, { routingNumber: '', routingNumberConfirmation: '', accountNumber: '', accountNumberConfirmation: '' });
  } catch (error) {
    message.text = error.message;
    message.error = true;
  } finally { isSaving.value = false; }
};
const removeAccount = async () => {
  try {
    await request('/creator/payout-bank-account', { method: 'DELETE' });
    bankAccount.value = null;
    isEditing.value = true;
    showRemoveConfirm.value = false;
    message.text = 'Payout account removed.';
    message.error = false;
  } catch (error) { message.text = error.message; message.error = true; }
};
onMounted(load);
</script>

<style scoped>
.app-scroll { scrollbar-width: thin; scrollbar-color: rgba(113,113,122,.34) transparent; }
.app-scroll::-webkit-scrollbar { width: 7px; height: 7px; }
.app-scroll::-webkit-scrollbar-track { background: transparent; }
.app-scroll::-webkit-scrollbar-thumb { border-radius: 999px; background: rgba(113,113,122,.32); }
</style>
